<template>
  <div class="holder-analysis-container">
    <h3>行业板块股东数列表</h3>
    <div class="input-section">
      <div class="input-row">
        <input 
          v-model="plateName" 
          placeholder="请输入板块名称，例如：电子信息、医药生物等" 
          class="plate-input"
          @keyup.enter="doInputPlateName"
          @focus="selectAllOnFocus"
        />
        <button @click="doInputPlateName" class="analyze-btn">分析</button>
        <div class="year-limit-selector">
          <label class="radio-label">
            <input 
              type="radio" 
              v-model="internalYearLimit" 
              :value="5" 
              class="radio-input"
            />
            <span class="radio-text">5年</span>
          </label>
          <label class="radio-label">
            <input 
              type="radio" 
              v-model="internalYearLimit" 
              :value="3" 
              class="radio-input"
            />
            <span class="radio-text">3年</span>
          </label>
        </div>
      </div>
    </div>
    <div v-if="plateData" class="result-section">
      <h4>分析结果</h4>
      <!-- 显示板块名称和数据统计 -->
      <div class="plate-info">
        <div class="info-item">
          <span class="info-label">行业板块名称:</span>
          <span class="info-value">{{ plateData.plateName }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">股票数:</span>
          <span class="info-value">{{ getDataByYearLimit().length }}</span>
        </div>
      </div>
      
      <!-- 以表格形式显示数据 -->
      <div class="table-container">
        <table class="data-table">
          <thead>
            <tr>
              <th>序号</th>
              <th>股票名称</th>
              <th>股票代码</th>
              <th>3年分数</th>
              <th>5年分数</th>
              <th>户均（万）</th>
              <th>现股东数</th>
              <th>现股东日期</th>
              <th>最少股东数</th>
              <th>最少股东日期</th>
              <th>最多股东数</th>
              <th>最多股东日期</th>
              <th>上市日期</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in getDataByYearLimit()" :key="item.id">
              <td>{{ index + 1 }}</td>
              <td>
                <span class="width-70">
                  <a :href="$router.resolve({ name: 'SingleStockHolder', params: { stockCode: item.stockCode, yearLimit: internalYearLimit } }).href" target="_blank" class="stock-link">{{ item.stockName }}</a>
                </span>
                <span class="seperator">|</span>
                <span><a :href="$router.resolve({ name: 'HealthCheck', params: { stockCode: item.stockCode } }).href" target="_blank" class="stock-link">检</a></span>
              </td>
              <td>{{ item.stockCode }}</td>
              <td :class="getRankingScoreClass(parseFloat(item.score_3y))" class="text-right">{{ parseFloat(item.score_3y).toFixed(2) }}</td>
              <td :class="['divider', getRankingScoreClass(parseFloat(item.score_5y))]" class="text-right">{{ parseFloat(item.score_5y).toFixed(2) }}</td>
              <td class="text-right">{{ (parseFloat(item.newestCap) / 10000).toFixed(2) }}</td>
              <td class="text-right">
                <span>{{ formatNumber(item.newestNum) }}</span>
                <span class="seperator-left">|</span>
                <span class="width-40">{{ parseFloat(item.newestNumChange).toFixed(0) }}%</span>
              </td>
              <td>{{ item.newestDate }}</td>
              <td class="text-right">{{ formatNumber(item.lowestNum) }}</td>
              <td>{{ item.lowestDate }}</td>
              <td class="text-right">{{ formatNumber(item.highestNum) }}</td>
              <td>{{ item.highestDate }}</td>
              <td>{{ item.listingDate }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import mockApi from '@/api/mock';
import { formatNumber, getRankingScoreClass } from '@/utils/utils';
import '@/assets/common-styles.css';

export default {
  name: 'IndustryPlateHolders',
  props: {
    plateId: {
      type: [Number, String],
      default: 0
    },
    yearLimit: {
      type: [String, Number],
      default: 5
    }
  },
  data() {
      // 设置internalYearLimit为默认值5，不再从localStorage读取
      return {
        internalYearLimit: 5, // 默认选中5年
        plateData: null,
        realPlateId: 0,
        plates: [], // 存储行业列表
        plateName: '' // 添加plateName用于存储用户输入的板块名称
      };
    },
    computed: {
    holderData() {
      // 根据internalYearLimit的值来决定使用哪个数据
      if (!this.plateData) {
        return null;
      }
      
      if (this.internalYearLimit === 3) {
        return this.plateData.stocks_3y || null;
      } else if (this.internalYearLimit === 5) {
        return this.plateData.stocks_5y || null;
      }
      
      // 默认返回5年数据
      return this.plateData.stocks_5y || null;
    }
  },
  watch: {
    realPlateId: {
      handler(newVal, oldVal) {
        // 当realPlateId发生变化时，更新路由参数
        console.log("realPlateId change to ", newVal, " oldVal: ", oldVal);
        if (newVal != oldVal) {
          this.plateName = '';
          this.fetchPlateData(newVal);
        }
      }
    },
    internalYearLimit: {
      handler(newVal) {
        // 当internalYearLimit发生变化时，更新路由参数
          
        // 因为holderData依赖于internalYearLimit，它会自动更新
        this.$nextTick(() => {
          console.log("internalYearLimit change to ", newVal, " current yearLimit: ", this.yearLimit);
          if (this.yearLimit != newVal) {
            const newParams = {
              plateId: this.plateId,
              yearLimit: newVal.toString()
            };
            this.$router.replace({ 
              name: 'IndustryPlateHolders',
              params: newParams
            });
          }
          this.updateTableData();
        });
      }
    },
    // 监听路由参数变化
    '$route'(to, from) {
      if (to.name !== 'IndustryPlateHolders') {
        return;
      }
      console.log("IndustryPlateHolders: route change from ", from, " to ", to, " plateId: ", this.plateId);
      this.internalYearLimit = to.params.yearLimit ? parseInt(to.params.yearLimit) : 5;
      this.realPlateId = to.params.plateId ? parseInt(to.params.plateId) : 0;
    }
  },
  async mounted() {
    // 组件挂载后立即获取板块列表
    console.log("IndustryPlateHolders: mounted plateId: ", this.plateId, " realPlateId: ", this.realPlateId, " yearLimit: ", this.yearLimit);
    await this.fetchPlates();
    this.realPlateId = this.plateId;
    if (this.yearLimit) {
      this.internalYearLimit = parseInt(this.yearLimit);
    }
  },
  beforeDestroy() {
    // 不再需要移除storage事件监听器
  },
  methods: {
    getRankingScoreClass,
    async fetchPlates() {
      try {
        const response = await mockApi.getPlates();
        if (response.success) {
          this.plates = response.data;
        }
      } catch (error) {
        console.error('获取行业列表失败:', error);
      }
    },
    async fetchPlateData(plateId) {
      if (plateId <= 0) {
        return;
      }
      try {
        const response = await mockApi.getPlateStocksScore(plateId);
        if (response.success) {
          this.plateData = response.data;
          // 设置板块名称
          this.plateName = response.data.plateName;
        }
      } catch (error) {
        console.error('获取板块数据失败:', error);
      }
    },
    async doInputPlateName() {
      if (!this.plateName) {
        alert('请输入板块名称');
        return;
      }
      
      // 查找匹配的板块ID
      const plate = this.plates.find(p => p.plateName === this.plateName);
      if (plate) {
        // await this.fetchPlateData(plate.id);
        // 重定向URL以包含plateId参数
        if (this.$router) {
          // 构建新的路由参数，保留现有的yearLimit参数
          const newParams = {
            plateId: plate.id,
            ...(this.$route.params.yearLimit && { yearLimit: this.$route.params.yearLimit })
          };
          this.$router.replace({ 
            name: 'IndustryPlateHolders', 
            params: newParams
          }).catch(err => {
            // 忽略导航重复的错误
            if (err.name !== 'NavigationDuplicated') {
              console.error('路由导航错误:', err);
            }
          });
        }
      } else {
        alert('未找到匹配的板块: ' + this.plateName);
      }
    },
    getDataByYearLimit() {
      // 根据internalYearLimit返回相应的数据
      if (!this.plateData) return [];
      
      if (this.internalYearLimit === 5) {
        return this.plateData.stocks_5y || [];
      } else {
        return this.plateData.stocks_3y || [];
      }
    },
    updateTableData() {
      // 强制更新表格数据
      // 在Vue中，当我们修改了响应式数据后，通常不需要手动触发更新
      // 但在这个场景中，我们可以通过触发一个虚拟的更新来确保视图刷新
      this.$forceUpdate();
    },
    // 输入框获得焦点时选中所有内容
    selectAllOnFocus(event) {
      this.$nextTick(() => {
        event.target.select();
      });
    },
    
    formatNumber
  }
};
</script>

<style scoped>
.holder-analysis-container {
  padding: 20px;
  max-width: 100vw;
  overflow-x: hidden;
  box-sizing: border-box;
}

.input-section {
  margin-bottom: 20px;
  max-width: 100%;
  overflow-x: hidden;
  box-sizing: border-box;
}

.input-section label {
  display: block;
  margin-bottom: 10px;
  font-weight: 500;
  color: #2c3e50;
}

.input-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  max-width: 100%;
  width: 100%;
  box-sizing: border-box;
}

.plate-input {
  flex: 1;
  min-width: 200px;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1em;
}

.analyze-btn {
  padding: 10px 20px;
  background-color: #42b983;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.3s ease;
  white-space: nowrap;
}

.analyze-btn:hover {
  background-color: #359268;
}

.year-limit-selector {
  display: flex;
  align-items: center;
  gap: 15px;
}

.radio-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 1em;
  color: #2c3e50;
}

.radio-input {
  margin-right: 5px;
  transform: scale(1.2);
}

.radio-text {
  white-space: nowrap;
}

.result-section {
  margin-top: 20px;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.result-section h4 {
  margin-top: 0;
  color: #2c3e50;
}

.plate-info {
  margin-bottom: 20px;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 50px; /* 增加两个数据块之间的距离 */
  flex-wrap: wrap;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 10px; /* label和值之间的间距 */
}

.info-label {
  white-space: nowrap;
}

.info-value {
  font-weight: bold;
}

.table-container {
  overflow-x: auto;
  max-width: 100%;
}

.data-table {
  width: 100%;
  max-width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
}

.data-table th,
.data-table td {
  padding: 12px 15px;
  text-align: left;
  border-bottom: 1px solid #ddd;
}

/* 覆盖默认左对齐，支持右对齐 */
.data-table td.text-right {
  text-align: right;
}

.data-table th {
  background-color: #f8f9fa;
  font-weight: bold;
  color: #2c3e50;
}

.data-table th.divider {
  border-left: 2px solid #42b983;
}

.data-table td.divider {
  border-left: 1px solid #ffffff;
}

.data-table tbody tr:hover {
  background-color: #f5f5f5;
}

.data-table td {
  font-size: 0.9em;
}




@media (max-width: 768px) {
  .input-row {
    flex-direction: column;
    align-items: stretch;
  }
  
  .plate-input {
    min-width: unset;
  }
  
  .year-limit-selector {
    justify-content: center;
    padding: 10px 0;
  }
  
  .data-table {
    font-size: 0.8em;
  }
  
  .data-table th,
  .data-table td {
    padding: 8px 10px;
  }
}
</style>